import { aliQtSignOut, aliQtSignUp, initAliQt, reportAliQtEvent, setAliQtGlobalProperties } from './ali-qt';
import {
  dataFinderGlobalProperties,
  reportEvent as dataRangersReportEvent,
  setRegisterPage as dataRangersSetRegisterPage,
  dataRangersSignOut,
  dataRangersSignUp,
  initDataFinder,
} from './data-finder-event';
import { DATA_FINDER_PLATFORM_TYPES, type DataFinderPlatformType } from './platform-type';

export { resolveDataFinderPlatformType } from './platform-type';

type SensorProperties = Record<string, string | boolean | number>;
type SensorProfile = {
  uid?: string;
  language?: string;
  is_member?: boolean;
  member_type?: string;
};

type SensorEvent =
  | [
      'track',
      {
        eventName: string;
        properties?: SensorProperties;
      },
    ]
  | ['registerPage', Record<string, string | boolean>]
  | ['logout'];

const IDENTITY_TIMEOUT_MS = 3000;
let hasLoaded = false;
let isIdentityReady = false;
let currentUserId = '';
let identityTimeout: ReturnType<typeof setTimeout> | undefined;
const unReportEvent: SensorEvent[] = [];
const pendingPageViews: SensorProperties[] = [];

const finishIdentity = () => {
  clearTimeout(identityTimeout);
  identityTimeout = undefined;
  if (isIdentityReady) return;
  queueMicrotask(() => {
    if (isIdentityReady) return;
    isIdentityReady = true;
    for (const properties of pendingPageViews.splice(0)) {
      reportEvent('page_view', properties);
    }
  });
};

export const initSensors = (platformType: DataFinderPlatformType = DATA_FINDER_PLATFORM_TYPES.web) => {
  if (hasLoaded) return;
  initDataFinder(platformType);
  initAliQt(platformType, dataFinderGlobalProperties);
  hasLoaded = true;
  identityTimeout = setTimeout(sensorsSignOut, IDENTITY_TIMEOUT_MS);
  for (const [sensorEvent, properties] of unReportEvent.splice(0)) {
    if (sensorEvent === 'track') {
      reportEvent(properties.eventName, properties.properties);
    } else if (sensorEvent === 'registerPage') {
      setRegisterPage(properties);
    } else {
      sensorsSignOut();
    }
  }
};

const checkSensor = (event: SensorEvent) => {
  if (!hasLoaded) {
    unReportEvent.push(event);
    return false;
  }
  return true;
};

export function reportEvent(eventName: string, properties?: SensorProperties) {
  const capturedProperties = { ...properties };
  if (!checkSensor(['track', { eventName, properties: capturedProperties }])) return;
  if (eventName === 'page_view' && !isIdentityReady) {
    pendingPageViews.push(capturedProperties);
    return;
  }
  const eventProperties = { ...dataFinderGlobalProperties, ...capturedProperties, uid: currentUserId };
  dataRangersReportEvent(eventName, eventProperties);
  reportAliQtEvent(eventName, eventProperties);
}

export function setRegisterPage(profile: SensorProfile) {
  if (!checkSensor(['registerPage', { ...profile }])) return;
  const properties = {
    ...(profile.uid && profile.uid !== currentUserId ? { is_member: false, member_type: 'free' } : {}),
    ...profile,
    uid: profile.uid || currentUserId,
  };
  if (profile.uid) {
    currentUserId = profile.uid;
    dataRangersSignUp(currentUserId);
    aliQtSignUp(currentUserId);
  }
  dataRangersSetRegisterPage(properties);
  setAliQtGlobalProperties(properties);
  if (profile.uid) finishIdentity();
}

export function sensorsSignUp(id: string) {
  if (id) setRegisterPage({ uid: id });
}

export function sensorsSignOut() {
  if (!checkSensor(['logout'])) return;
  currentUserId = '';
  dataRangersSignOut();
  aliQtSignOut();
  const properties = { uid: '', is_member: false, member_type: 'free' };
  dataRangersSetRegisterPage(properties);
  setAliQtGlobalProperties(properties);
  finishIdentity();
}
